import { router } from "@inertiajs/react"
import { useState, useEffect } from "react"
import { FileText, Folder, FolderUp, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown, Plus } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu"
import { FileContextMenu } from "./FileContextMenu"
import { FolderContextMenu } from "./FolderContextMenu"

type SortField = "name" | "clientModified" | "serverModified"
type SortDirection = "asc" | "desc"

const SORT_LABELS: Record<SortField, string> = {
  name: "Name",
  clientModified: "Created",
  serverModified: "Updated",
}

interface FileEntry {
  name: string
  path: string
  type: "file" | "folder"
  clientModified?: string
  serverModified?: string
  tags?: Record<string, string>
}

type ViewMode = "files" | "recent"

interface FileBrowserProps {
  rootFolder: string
  currentPath: string
  entries: FileEntry[]
  onNavigate: (path: string) => void
  onRefresh: () => void
  viewMode?: ViewMode
}

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") || ""
}

export function FileBrowser({ rootFolder, currentPath, entries, onNavigate, onRefresh, viewMode = "files" }: FileBrowserProps) {
  const [sortField, setSortField] = useState<SortField>("clientModified")
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc")
  const [renamingPath, setRenamingPath] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState("")
  const [favoritePaths, setFavoritePaths] = useState<Set<string>>(new Set())
  const [showHidden, setShowHidden] = useState(false)

  useEffect(() => {
    checkFavorites()
  }, [entries])

  async function checkFavorites() {
    const paths = entries.map((e) => e.path)
    if (paths.length === 0) return
    try {
      const res = await fetch("/api/favorites/check", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
        body: JSON.stringify({ paths }),
      })
      const data = await res.json()
      setFavoritePaths(new Set(data.favorited || []))
    } catch {
      // ignore
    }
  }

  async function handleToggleFavorite(path: string, name: string) {
    const isFav = favoritePaths.has(path)
    try {
      if (isFav) {
        await fetch("/api/favorites", {
          method: "DELETE",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
          body: JSON.stringify({ path }),
        })
        setFavoritePaths((prev) => { const next = new Set(prev); next.delete(path); return next })
      } else {
        const entry = entries.find((e) => e.path === path)
        await fetch("/api/favorites", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
          body: JSON.stringify({ path, name, item_type: entry?.type || "file" }),
        })
        setFavoritePaths((prev) => new Set(prev).add(path))
      }
    } catch {
      // ignore
    }
  }

  function compareEntries(a: FileEntry, b: FileEntry): number {
    let cmp: number
    if (sortField === "name") {
      cmp = a.name.localeCompare(b.name)
    } else {
      const aVal = a[sortField] ? new Date(a[sortField]!).getTime() : 0
      const bVal = b[sortField] ? new Date(b[sortField]!).getTime() : 0
      cmp = aVal - bVal
    }
    return sortDirection === "asc" ? cmp : -cmp
  }

  function handleSortClick(field: SortField) {
    if (field === sortField) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortField(field)
    }
  }

  const hasHiddenEntries = entries.some((e) => e.name.startsWith("."))
  const visibleEntries = showHidden ? entries : entries.filter((e) => !e.name.startsWith("."))
  const folders = visibleEntries.filter((e) => e.type === "folder").sort((a, b) => a.name.localeCompare(b.name))
  const files = visibleEntries.filter((e) => e.type === "file").sort(compareEntries)
  const sortedEntries = [...folders, ...files]

  // Build breadcrumbs relative to root folder
  const rootParts = rootFolder.split("/").filter(Boolean)
  const isDropboxRoot = rootParts.length === 0
  const rootName = rootParts.length > 0 ? rootParts[rootParts.length - 1] : "Root"
  const allParts = currentPath.split("/").filter(Boolean)
  const breadcrumbs = allParts.slice(rootParts.length)

  function handleOpenFile(path: string) {
    const editorPath = path.replace(/^\/+/, "")
    const encodedPath = editorPath.split("/").map(segment => encodeURIComponent(segment)).join("/")
    router.visit(`/files/${encodedPath}`)
  }

  async function handleRename(path: string, newName: string) {
    const parts = path.split("/")
    parts[parts.length - 1] = newName
    const newPath = parts.join("/")

    await fetch("/api/files/move", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCSRFToken(),
      },
      body: JSON.stringify({ from_path: path, to_path: newPath }),
    })
    onRefresh()
  }

  async function handleDelete(path: string) {
    const encodedPath = path.replace(/^\/+/, "").split("/").map(s => encodeURIComponent(s)).join("/")
    await fetch(`/api/files/${encodedPath}`, {
      method: "DELETE",
      headers: { "X-CSRF-Token": getCSRFToken() },
    })
    onRefresh()
  }

  async function handleMove(fromPath: string, toPath: string) {
    await fetch("/api/files/move", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCSRFToken(),
      },
      body: JSON.stringify({ from_path: fromPath, to_path: toPath }),
    })
    onRefresh()
  }

  function handleBreadcrumbClick(index: number) {
    const targetParts = [...rootParts, ...breadcrumbs.slice(0, index + 1)]
    onNavigate("/" + targetParts.join("/"))
  }

  function handleStartFolderRename(path: string, name: string) {
    setRenamingPath(path)
    setRenameValue(name)
  }

  function handleFinishFolderRename(path: string) {
    if (renameValue && renameValue !== path.split("/").pop()) {
      handleRename(path, renameValue)
    }
    setRenamingPath(null)
  }

  function renderFileRow(entry: FileEntry) {
    const isFolder = entry.type === "folder"
    const isFolderRenaming = isFolder && renamingPath === entry.path

    return (
      <div
        key={entry.path}
        className="group flex items-center gap-3 px-4 py-3 hover:bg-accent/50 cursor-pointer"
        onClick={() => {
          if (isFolderRenaming) return
          isFolder ? onNavigate(entry.path) : handleOpenFile(entry.path)
        }}
        data-testid={`file-row-${entry.name}`}
      >
        {isFolder ? (
          <Folder className="h-5 w-5 text-amber-500 shrink-0" />
        ) : (
          <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          {isFolderRenaming ? (
            <input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleFinishFolderRename(entry.path)
                if (e.key === "Escape") setRenamingPath(null)
              }}
              onBlur={() => handleFinishFolderRename(entry.path)}
              onClick={(e) => e.stopPropagation()}
              className="w-full text-sm font-medium bg-transparent border-b border-primary outline-none py-0.5"
              autoFocus
              data-testid="folder-rename-input"
            />
          ) : (
            <p className="truncate text-sm font-medium">{entry.name}</p>
          )}
        </div>
        {entry.tags && Object.entries(entry.tags).map(([key, value]) => (
          <Badge key={key} variant="secondary" className="text-xs">
            {value}
          </Badge>
        ))}
        <div className="opacity-0 group-hover:opacity-100" onClick={(e) => e.stopPropagation()}>
          {isFolder ? (
            <FolderContextMenu
              path={entry.path}
              name={entry.name}
              rootFolder={rootFolder}
              onRename={handleRename}
              onDelete={handleDelete}
              onMove={handleMove}
              isRenaming={isFolderRenaming}
              onStartRename={() => handleStartFolderRename(entry.path, entry.name)}
              onStopRename={() => setRenamingPath(null)}
              isFavorite={favoritePaths.has(entry.path)}
              onToggleFavorite={handleToggleFavorite}
            />
          ) : (
            <FileContextMenu
              path={entry.path}
              name={entry.name}
              rootFolder={rootFolder}
              onRename={handleRename}
              onDelete={handleDelete}
              onMove={handleMove}
              isFavorite={favoritePaths.has(entry.path)}
              onToggleFavorite={handleToggleFavorite}
            />
          )}
        </div>
      </div>
    )
  }

  function handleNewFile() {
    // Navigate to new file with current path as destination query param
    const dest = encodeURIComponent(currentPath)
    router.visit(`/new?dest=${dest}`)
  }

  return (
    <div className="mx-auto max-w-3xl lg:max-w-5xl p-6" data-testid="file-browser">
      {/* Top toolbar: breadcrumbs left, actions right */}
      <div className="flex items-center justify-between gap-4 mb-4">
        {/* Left: breadcrumbs */}
        <div className="flex items-center gap-1 text-sm min-w-0">
          {!(isDropboxRoot && breadcrumbs.length === 0) ? (
            <>
              {!isDropboxRoot && (
                <>
                  <button
                    onClick={() => router.visit("/files_root")}
                    className="p-1 text-muted-foreground hover:text-foreground shrink-0"
                    title="Navigate above root folder"
                  >
                    <FolderUp className="h-4 w-4" />
                  </button>
                  <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
                </>
              )}
              <button
                onClick={() => onNavigate(rootFolder)}
                className={`shrink-0 ${breadcrumbs.length === 0 ? "font-medium text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                {rootName}
              </button>
              {breadcrumbs.map((part, i) => (
                <span key={i} className="flex items-center gap-1 min-w-0">
                  <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
                  <button
                    onClick={() => handleBreadcrumbClick(i)}
                    className={`truncate ${i === breadcrumbs.length - 1 ? "font-medium text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  >
                    {part}
                  </button>
                </span>
              ))}
            </>
          ) : (
            <div />
          )}
        </div>

        {/* Right: toggle + sort + new */}
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Recent / Files toggle */}
          <div className="flex items-center rounded-md bg-muted p-0.5">
            <button
              onClick={() => router.visit("/recent-files")}
              className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                viewMode === "recent"
                  ? "bg-white text-foreground dark:bg-background"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Recent
            </button>
            <button
              onClick={() => router.visit("/files")}
              className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                viewMode === "files"
                  ? "bg-white text-foreground dark:bg-background"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Files
            </button>
          </div>

          {/* Sort dropdown (icon only) */}
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center justify-center h-7 w-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors cursor-pointer">
              <ArrowUpDown className="h-3.5 w-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {(Object.keys(SORT_LABELS) as SortField[]).map((field) => (
                <DropdownMenuItem key={field} onClick={() => handleSortClick(field)}>
                  <span className={sortField === field ? "font-medium" : ""}>{SORT_LABELS[field]}</span>
                  {sortField === field && (
                    sortDirection === "asc"
                      ? <ArrowUp className="h-3 w-3 ml-auto" />
                      : <ArrowDown className="h-3 w-3 ml-auto" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* New file button */}
          <button
            onClick={handleNewFile}
            className="flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            title="New file"
          >
            <Plus className="h-3.5 w-3.5" />
            New
          </button>
        </div>
      </div>

      {sortedEntries.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground" data-testid="empty-folder">
          This folder is empty
        </div>
      ) : (
        <div className="rounded-lg border bg-card shadow-xs overflow-hidden divide-y">
          {sortedEntries.map(renderFileRow)}
        </div>
      )}

      {hasHiddenEntries && (
        <div className="mt-3 text-center">
          <button
            onClick={() => setShowHidden(!showHidden)}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {showHidden ? "Hide hidden files" : "Show hidden files"}
          </button>
        </div>
      )}
    </div>
  )
}
